import { Button } from "@mui/material";
import {
  BOX_SHADOW_ALERT,
  BREAKPOINT_DESKTOP_XL,
} from "../constants/constants";
import { NotificationCard } from "components/SimpleComponents/TopBanner/NotificationCard";
import { LockClock, LockReset } from "@mui/icons-material";
import { css } from "styled-components/macro";
import { blueButton } from "constants/colors";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { BREAKPOINT_MOBILE } from "reports/constants/constants";
import * as Sentry from "@sentry/react";
import { atom, useAtom } from "jotai";
import { useMount } from "react-use";

export const authExpiredAtom = atom(false);

const scale = (window.innerWidth / BREAKPOINT_DESKTOP_XL) * 0.8;
/** Popup toast message that says "Authentication failed" and prompts for login */
export const AuthenticationFailedIndicator = () => {
  const [authExpired] = useAtom(authExpiredAtom);
  return authExpired ? <AuthenticationFailedIndicatorContent /> : null;
};

function AuthenticationFailedIndicatorContent() {
  const [, setAuthExpired] = useAtom(authExpiredAtom);

  const { loginWithRedirect } = useAuth0();
  useMount(() => {
    Sentry.captureException(new Error(`AUTH_EXPIRED_INDICATOR`));
  });
  const { t } = useFlightTranslation();
  return (
    <NotificationCard
      className={`authenticationFailedIndicator doNotPrint`}
      {...{
        message: (
          <div className="message">
            <div className="title">{t("Your session has expired")},</div>
            <div className="body">{t("click to reload")}:</div>
          </div>
        ),
        icon: <LockClock />,
        variant: "warning",
        customCss: css`
          opacity: 0.8;
          position: fixed;
          bottom: 12px;
          right: 12px;
          box-shadow: ${BOX_SHADOW_ALERT};
          height: fit-content;
          display: flex;
          flex-direction: column;
          @media (min-width: ${BREAKPOINT_MOBILE}px) {
            flex-direction: row;
            gap: 1.5em;
          }
          align-items: flex-end;
          padding: 12px;
          font-size: 14px;
          && {
            margin: 0;
          }
          .icon-and-message {
            margin: auto;
            align-items: start;
          }
          .title {
            font-weight: bold;
          }
          button.MuiButton-root {
            text-transform: none;
            font-size: 16px;
            opacity: 1;
            transition: opacity 300ms ease;
            &:hover {
              opacity: 0.7;
            }
            &.btnUpdate {
              color: #fff;
              background-color: ${blueButton};
            }
          }
          @media (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
            transform: scale(${scale});
            transform-origin: bottom left;
            padding: ${12 * scale}px;
            bottom: ${12 * scale}px;
            right: ${12 * scale}px;
            gap: ${12 * scale}px;
            font-size: ${14 * scale}px;
            button.MuiButton-root {
              font-size: ${16 * scale}px;
            }
            svg {
              width: ${24 * scale}px;
              height: ${24 * scale}px;
            }
          }
        `,
        ActionButton: (
          <div style={{ display: "flex", gap: 16 * Math.max(1, scale) }}>
            <Button
              className="btnUpdate"
              variant="outlined"
              onClick={() => {
                setAuthExpired(false);
                loginWithRedirect({
                  appState: {
                    targetUrl:
                      window.location.pathname + window.location.search,
                  },
                });
              }}
              endIcon={<LockReset />}
            >
              Sign In
            </Button>
          </div>
        ),
      }}
    />
  );
}
